import React from 'react';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ChartOptions,
	TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { IIncident } from 'types/Branches';
import { IGraphAgency, IGraphAgencyCategory } from 'types/VehicleIncidents';
import { createCategoryAcronym } from 'utils/createCategoryAcronym';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend
);

export interface IIncidentAgenciesAtAGlanceGraph {
	agencyData: IGraphAgency[] | undefined;
}

const IncidentAgenciesAtAGlanceGraph: React.FC<
	IIncidentAgenciesAtAGlanceGraph
> = ({ agencyData }) => {
	const options: ChartOptions<'bar'> = {
		plugins: {
			legend: {
				align: 'start',
				position: 'bottom',
				fullSize: true,
				labels: {
					usePointStyle: true,
					pointStyle: 'rect',
					boxHeight: 8,
					font: {
						size: 12,
					},
				},
			},
			tooltip: {
				displayColors: true,
				callbacks: {
					//removes default tooltip body information
					label() {
						return '';
					},
					afterBody(context: TooltipItem<'bar'>[]) {
						const currentSeverity = agencyData?.find(
							(currentMonthData: IGraphAgency) =>
								currentMonthData.name === context[0].label
						);

						if (currentSeverity) {
							const resultString = [];

							if (currentSeverity.total > 0) {
								resultString.push(`Incidents: ${currentSeverity.total}`);
							}

							//format and sort incidents to push to string
							const incidents = currentSeverity.categories
								.map((category: IGraphAgencyCategory) => {
									return {
										name: category.name,
										incidents: category.total,
									};
								})
								.filter((category: IIncident) => category.incidents !== 0)
								.sort(
									(a: IIncident, b: IIncident) => b.incidents - a.incidents
								);

							incidents.forEach((incident: IIncident) =>
								resultString.push(` - ${incident.name}: ${incident.incidents}`)
							);

							if (currentSeverity.open > 0) {
								if (currentSeverity.total > 0) {
									resultString.push('');
								}

								resultString.push(`Open: ${currentSeverity.open}`);
							}

							return resultString;
						} else {
							return [`${context[0].dataset.label}: ${context[0].raw}`];
						}
					},
				},
				padding: {
					left: 10,
					right: 30,
					y: 10,
				},
				borderColor: 'rgba(52, 64, 84, 0.5)',
				borderWidth: 1,
				backgroundColor: '#FFFFFF',
				titleColor: '#344054',
				bodyColor: '#344054',
				bodyFont: {
					size: 11,
				},
			},
		},
		responsive: true,
		interaction: {
			mode: 'index' as const,
			intersect: false,
		},
		scales: {
			x: {
				stacked: true,
			},
			y: {
				stacked: true,
			},
		},
		maintainAspectRatio: false,
		// @ts-ignore - ChartJS types are simply incorrect
		barThickness: 24,
	};

	const data = {
		labels: agencyData?.map((agency: IGraphAgency) =>
			createCategoryAcronym(agency.name)
		),
		datasets: [
			{
				label: 'Agencies total',
				data: agencyData?.map((agency: IGraphAgency) => agency.total),
				backgroundColor: '#FAA431',
				stack: 'Stack 0',
			},
		],
	};

	return <Bar options={options} data={data} redraw={true} />;
};

export default IncidentAgenciesAtAGlanceGraph;
